import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, CheckCircle2, Play, ShieldCheck, Target, Eye } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Container, Counter, Eyebrow, Reveal, SectionHeading, ArrowFwd } from "../components/shared/Shared";
import { useLang } from "../context/LanguageContext";
import HomeSections from "./HomeSections";

const useTypewriter = (words, speed = 90, pause = 1600) => {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);
  useEffect(() => { setText(""); setIndex(0); setDeleting(false); }, [words]);
  useEffect(() => {
    const word = words[index % words.length];
    let t;
    if (!deleting && text === word) t = setTimeout(() => setDeleting(true), pause);
    else if (deleting && text === "") { setDeleting(false); setIndex((i) => (i + 1) % words.length); }
    else t = setTimeout(() => setText(word.slice(0, text.length + (deleting ? -1 : 1))), deleting ? speed / 2 : speed);
    return () => clearTimeout(t);
  }, [text, deleting, index, words, speed, pause]);
  return text;
};

const Hero = ({ onQuote }) => {
  const { t, c } = useLang();
  const h = t.hero;
  const word = useTypewriter(c.HERO_WORDS);
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-[#EEF6F8] via-white to-white pt-36 pb-16 sm:pt-44 lg:pb-24" data-testid="hero-section">
      <div className="absolute inset-0 hero-grid-dark opacity-60" />
      <div className="absolute -start-32 top-24 h-96 w-96 rounded-full bg-brand-teal/15 blur-3xl" />
      <div className="absolute end-0 top-0 h-96 w-96 rounded-full bg-brand-orange/10 blur-3xl" />
      <Container className="relative grid items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <div className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-brand-teal/20 bg-white px-4 py-1.5 text-xs font-semibold text-brand-teal shadow-sm"><Award className="h-4 w-4 text-brand-orange" /> {h.badge}</div>
          <h1 className="animate-fade-up mt-6 font-heading text-4xl font-bold leading-[1.12] tracking-tight text-brand-navy sm:text-5xl lg:text-[4.1rem]" style={{ animationDelay: "80ms" }}>
            {h.title1}<br />
            {h.your}{" "}
            <span className="relative inline-block text-brand-teal">{word}<span className="mx-0.5 inline-block h-[0.9em] w-[3px] translate-y-1 animate-blink bg-brand-orange align-middle" /></span>
            {h.partner && <><br />{h.partner}</>}
          </h1>
          <p className="animate-fade-up mt-6 max-w-xl text-lg leading-relaxed text-slate-600" style={{ animationDelay: "160ms" }}>{h.desc}</p>
          <div className="animate-fade-up mt-9 flex flex-wrap gap-4" style={{ animationDelay: "240ms" }}>
            <button type="button" onClick={onQuote} data-testid="hero-quote-button" className="btn-primary bg-brand-orange text-white hover:bg-orange-600">{t.nav.quote} <ArrowFwd className="h-4 w-4" /></button>
            <Link to="/about" data-testid="hero-about-link" className="btn-outline"><Play className="h-4 w-4 fill-current" /> {h.discover}</Link>
          </div>
          <div className="animate-fade-up mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500" style={{ animationDelay: "320ms" }}>
            {h.trust.map((x) => <span key={x} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-brand-teal" />{x}</span>)}
          </div>
        </div>
        <div className="relative lg:col-span-6">
          <div className="animate-fade-up relative mx-auto max-w-[560px]" style={{ animationDelay: "200ms" }}>
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-brand-teal/20 to-brand-orange/20 blur-2xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] shadow-2xl shadow-brand-navy/20 ring-1 ring-black/5">
              <img src={c.IMAGES.hero} alt="Digital network technology" className="aspect-[5/4] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 start-5 end-5 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-300">{h.weEnable}</p>
                <p className="mt-1 font-heading text-2xl font-bold">{h.edt}</p>
              </div>
            </div>
            <div className="animate-float absolute -start-6 top-10 hidden rounded-2xl bg-white p-4 shadow-xl shadow-brand-navy/10 ring-1 ring-black/5 sm:block">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal"><ShieldCheck className="h-5 w-5" /></span>
                <div><p className="text-xs text-slate-500">{h.securityFirst}</p><p className="font-heading text-lg font-bold text-brand-navy">{h.zeroTrust}</p></div>
              </div>
            </div>
            <div className="animate-float-slow absolute -bottom-6 -end-4 hidden rounded-2xl bg-white p-4 shadow-xl shadow-brand-navy/10 ring-1 ring-black/5 sm:block">
              <p className="text-xs text-slate-500">{h.retention}</p>
              <p className="font-heading text-3xl font-bold text-brand-navy" dir="ltr">98<span className="text-brand-orange">%</span></p>
              <div className="mt-2 h-1.5 w-32 overflow-hidden rounded-full bg-slate-100"><div className="h-full w-[98%] rounded-full bg-brand-teal" /></div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};

const Stats = () => {
  const { t, c } = useLang();
  return (
    <section className="relative z-10 -mt-2">
      <Container>
        <Reveal className="grid grid-cols-2 divide-slate-100 rounded-2xl border border-slate-100 bg-white shadow-xl shadow-brand-navy/5 md:grid-cols-4 md:divide-x md:rtl:divide-x-reverse">
          {c.STATS.map((s) => (
            <div key={s.label} className="px-6 py-8 text-center">
              <p className="font-heading text-4xl font-bold text-brand-navy lg:text-5xl" dir="ltr"><Counter value={s.value} suffix={s.suffix} /></p>
              <p className="mt-2 text-sm font-medium text-slate-500">{s.label}</p>
            </div>
          ))}
        </Reveal>
        <Reveal className="mt-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-sm font-semibold uppercase tracking-wider text-slate-400" delay={100}>
          <span className="text-xs tracking-[0.2em] text-slate-400">{t.hero.partners}</span>
          {c.PARTNERS.map((p) => <span key={p} className="font-heading text-base text-slate-500 transition-colors hover:text-brand-teal">{p}</span>)}
        </Reveal>
      </Container>
    </section>
  );
};

const EnableTabs = () => {
  const { t, c } = useLang();
  const s = t.sections;
  return (
    <section className="py-24" data-testid="enable-section">
      <Container>
        <SectionHeading eyebrow={s.enableEyebrow} title={s.enableTitle} description={s.enableDesc} align="center" />
        <Tabs defaultValue={c.ENABLE_TABS[0].key} className="mt-12">
          <TabsList className="mx-auto flex h-auto w-full max-w-3xl flex-wrap justify-center gap-2 bg-transparent p-0">
            {c.ENABLE_TABS.map((tab) => (
              <TabsTrigger key={tab.key} value={tab.key} data-testid={`enable-tab-${tab.key}`} className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors data-[state=active]:border-brand-teal data-[state=active]:bg-brand-teal data-[state=active]:text-white data-[state=active]:shadow-md">{tab.label}</TabsTrigger>
            ))}
          </TabsList>
          {c.ENABLE_TABS.map((tab) => (
            <TabsContent key={tab.key} value={tab.key} className="mt-10 animate-fade-in">
              <div className="grid items-center gap-10 overflow-hidden rounded-3xl border border-slate-100 bg-slate-50/60 p-6 lg:grid-cols-2 lg:p-10">
                <div className="overflow-hidden rounded-2xl"><img src={tab.image} alt={tab.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" /></div>
                <div>
                  <h3 className="font-heading text-3xl font-bold text-brand-navy">{tab.title}</h3>
                  <p className="mt-4 leading-relaxed text-slate-600">{tab.description}</p>
                  <ul className="mt-6 grid gap-3 sm:grid-cols-2">{tab.points.map((p) => <li key={p} className="flex items-center gap-2 text-sm font-medium text-slate-700"><CheckCircle2 className="h-4 w-4 shrink-0 text-brand-orange" />{p}</li>)}</ul>
                  <Link to="/contact" className="mt-8 inline-flex items-center gap-2 font-semibold text-brand-teal transition-colors hover:text-brand-teal-dark">{s.talkExpert} <ArrowFwd className="h-4 w-4" /></Link>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </Container>
    </section>
  );
};

export const MissionVision = () => {
  const { t, c } = useLang();
  const s = t.sections;
  return (
    <section className="bg-slate-50 py-24" data-testid="mission-vision-section">
      <Container className="grid gap-8 lg:grid-cols-2">
        {[{ icon: Target, label: s.mission, text: c.MISSION, accent: "bg-brand-teal" }, { icon: Eye, label: s.vision, text: c.VISION, accent: "bg-brand-orange" }].map((m, i) => (
          <Reveal key={m.label} delay={i * 120} className="card-hover relative overflow-hidden rounded-3xl bg-white p-10 shadow-sm ring-1 ring-slate-100">
            <span className={`absolute start-0 top-0 h-full w-1.5 ${m.accent}`} />
            <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${m.accent} text-white shadow-lg`}><m.icon className="h-7 w-7" /></span>
            <Eyebrow className="mt-8">{m.label}</Eyebrow>
            <h3 className="mt-3 font-heading text-2xl font-bold text-brand-navy">{s.our ? `${s.our} ${m.label}` : m.label}</h3>
            <p className="mt-4 leading-relaxed text-slate-600">{m.text}</p>
          </Reveal>
        ))}
      </Container>
    </section>
  );
};

const Home = ({ onQuote }) => (
  <>
    <Hero onQuote={onQuote} />
    <Stats />
    <EnableTabs />
    <MissionVision />
    <HomeSections onQuote={onQuote} />
  </>
);

export default Home;
